/**
 * One `ChannelMessage` row as the inbox reads it from `conversations.get` (spec 2.3 and 3.17,
 * written by B1): what WhatsApp carried for a turn, with its ticks and cost. Dates arrive as
 * Date over oRPC and as strings from JSON, so both are accepted.
 */
export interface ChannelMessageRow {
	id: string;
	wamid: string | null;
	direction: "IN" | "OUT";
	/** text, image, audio, voice, location, reaction, contacts, order, template, interactive.*, flow_reply ... */
	kind: string;
	view: string | null;
	productIds: string[];
	/** Outbound: the Graph body without the recipient. Inbound: `{ content, referral?, ... }`. */
	payload: unknown;
	mediaKey: string | null;
	templateName: string | null;
	replyToWamid: string | null;
	/** accepted | sent | delivered | read | played | failed | held | skipped | received */
	status: string;
	errorCode: number | null;
	errorTitle: string | null;
	pricingCategory: string | null;
	pricingType: string | null;
	costUsd: string | number | null;
	sentAt: DateLike;
	deliveredAt: DateLike;
	readAt: DateLike;
	playedAt: DateLike;
	failedAt: DateLike;
	createdAt: Date | string;
}

type DateLike = Date | string | null;

/**
 * The WhatsApp rows of one transcript message (`messages[].channelMessages` from
 * `conversations.get`). A conversation from before WhatsApp commerce, or another channel, has
 * none, and its turns render as before.
 */
export function channelRowsOf(message: object): ChannelMessageRow[] {
	if (!("channelMessages" in message)) {
		return [];
	}
	const rows = message.channelMessages;
	return Array.isArray(rows) ? (rows as ChannelMessageRow[]) : [];
}

/** How the shopper arrived (`Conversation.entry`, spec 2.4). */
export interface ConversationEntry {
	kind: "link" | "qr" | "ad" | "ice_breaker" | "organic";
	code?: string;
	storeExternalId?: string;
	adSourceId?: string;
	headline?: string;
	at?: string;
}

/** "From the Al Reem link", "From an ad: Fresh milk 2 for 1", or null for an organic chat. */
export function entryLabel(
	entry: unknown,
	storeName?: (externalId: string) => string | null,
): string | null {
	if (!entry || typeof entry !== "object" || !("kind" in entry)) {
		return null;
	}
	const value = entry as ConversationEntry;
	const branch = value.storeExternalId
		? (storeName?.(value.storeExternalId) ?? null)
		: null;
	switch (value.kind) {
		case "ad":
			return value.headline
				? `From an ad: ${value.headline}`
				: "From an ad";
		case "link":
		case "qr":
			return branch
				? `From the ${branch} ${value.kind === "qr" ? "QR code" : "link"}`
				: value.kind === "qr"
					? "From a QR code"
					: value.code
						? `From the ${value.code} link`
						: "From a link";
		case "ice_breaker":
			return "From a suggested question";
		default:
			return null;
	}
}
